<template>
  <div>
    <div class="accountsheet-list">
      <BaseTableSkeletonLoader v-if="isLoadingAccountSheets"></BaseTableSkeletonLoader>
      <v-card v-else>
        <v-data-table
          :dense="true"
          item-key="id"
          disableSort
          :headers="headers"
          :items="tableData"
          item-class="row-class"
          :disable-pagination="true"
          :mobile-breakpoint="0"
          :hide-default-header="isEmpty"
          :hide-default-footer="true"
          @click:row="onRowClick"
        >
          <template v-slot:no-data="">
            <DataTableEmpty
              v-if="isSuperOrAccountant"
              @create="onClickUpsertAccountSheet()"
              item="giảng phí"
              text="Chưa có giảng phí nào"
            ></DataTableEmpty>
          </template>
          <template v-slot:top="">
            <div class="d-flex pa-3 align-center">
              <span class="text-body-2"> Total: {{ tableData.length }} </span>
              <div class="flex-fill"></div>
              <div class="search-wrapper mr-2">
                <v-text-field
                  prepend-inner-icon="mdi-magnify"
                  type="search"
                  v-model="search"
                  dense
                  outlined
                  hide-details="auto"
                  clearable
                ></v-text-field>
              </div>
              <v-btn
                v-if="isSuperOrAccountant"
                elevation="0"
                color="primary"
                @click="onClickUpsertAccountSheet()"
              >
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </div>
          </template>
          <template v-slot:item.actions="{ item }">
            <div class="d-flex">
              <v-btn icon small>
                <v-icon small>mdi-eye</v-icon>
              </v-btn>
              <v-btn
                v-if="isSuperOrAccountant && !item.isLock"
                icon
                small
                @click.stop="onClickDeleteAccountSheet(item)"
              >
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
              <!-- <v-icon v-else small>mdi-lock</v-icon> -->
            </div>
          </template>
          <template v-slot:item.startDate="{ item }">
            {{ formatStartDateForAccountSheet(item.startDate) }}
          </template>
          <template v-slot:item.endDate="{ item }">
            {{ formatDate(item.endDate) }}
          </template>
          <template v-slot:item.status="{ item }">
            {{ getStatusText(item) }}
          </template>
        </v-data-table>
      </v-card>
    </div>
    <BaseDialog
      v-if="upsertAccountSheetDialog.isOpen"
      :isOpen="upsertAccountSheetDialog.isOpen"
      :title="upsertAccountSheetDialog.title"
      @close="upsertAccountSheetDialog.isOpen = false"
      @cancel="upsertAccountSheetDialog.onCancel"
      @confirm="upsertAccountSheetDialog.callback"
    >
      <v-form lazy-validation ref="upsertAccountSheetDialog">
        <v-card-text>
          <v-text-field
            autofocus
            label="Tiêu đề"
            v-model="upsertAccountSheetDialog.currentItem.title"
            :rules="[$rules.required]"
          ></v-text-field>
          <v-autocomplete
            :rules="[$rules.required]"
            v-model="upsertAccountSheetDialog.currentItem.accountant"
            item-text="name"
            :items="accountants"
            return-object
            item-value="id"
            label="Kế toán phụ trách"
            required
            dense
            outlined
            @change="onCourseChange"
          ></v-autocomplete>
          <date-selector
            label="Bắt đầu"
            :rules="[$rules.required]"
            :date="transformDateForDatePicker(upsertAccountSheetDialog.currentItem.startDate)"
            @change="onStartDateChange"
          ></date-selector>
          <date-selector
            label="Kết thúc"
            :rules="[$rules.required]"
            :date="transformDateForDatePicker(upsertAccountSheetDialog.currentItem.endDate)"
            @change="onEndDateChange"
          ></date-selector>

          <v-textarea
            rows="3"
            label="Ghi chú"
            v-model="upsertAccountSheetDialog.currentItem.note"
          ></v-textarea>
        </v-card-text>
      </v-form>
    </BaseDialog>
  </div>
</template>

<script>
import moment from 'moment'
import Breadcrumbs from '@/components/basic/Breadcrumbs.vue'
import DataTableEmpty from '@/components/DataTableEmpty.vue'
import BaseDialog from '@/components/base/BaseDialog'
import DateSelector from '@/components/basic/DateSelector.vue'
import { formatStartDateForAccountSheet, formatDate, transformDateForDatePicker } from '@/helpers'
import BaseTableSkeletonLoader from '@/components/base/BaseTableSkeletonLoader'

import { mapState, mapActions, mapGetters } from 'vuex'
export default {
  components: { DateSelector, Breadcrumbs, DataTableEmpty, BaseDialog, BaseTableSkeletonLoader },
  data() {
    return {
      search: '',
      upsertAccountSheetDialog: {
        currentItem: null,
        isOpen: false,
        title: '',
        valid: true,
        onCancel: () => {
          this.upsertAccountSheetDialog.isOpen = false
        },
        callback: () => {}
      }
    }
  },
  methods: {
    ...mapActions('accountsheet', [
      'fetchAccountSheets',
      'updateAccountSheet',
      'removeAccountSheet',
      'createAccountSheet'
    ]),
    ...mapActions('staff', ['fetchStaffs']),
    ...mapActions('accountant', ['fetchAccountants']),
    formatStartDateForAccountSheet,
    transformDateForDatePicker,
    formatDate,
    getStatusText(item) {
      if (item.isCanceled) return 'Đã thu hồi'
      if (item.isLock) return 'Đã khóa'
      if (item.isPublished) return 'Đã xuất bản'
      return 'Nháp'
    },
    onRowClick(item) {
      this.$router.push(`/account-sheets/${item.id}`)
    },
    onStartDateChange(value) {
      this.upsertAccountSheetDialog.currentItem.startDate = moment(
        value,
        'YYYY-MM-DD'
      ).toISOString()
    },
    onEndDateChange(value) {
      this.upsertAccountSheetDialog.currentItem.endDate = moment(value, 'YYYY-MM-DD')
        .endOf('day')
        .toISOString()
    },
    onClickUpsertAccountSheet(item) {
      this.upsertAccountSheetDialog.currentItem = item
        ? cloneDeep(item)
        : {
            title: 'Giảng phí tháng ' + (new Date().getMonth() + 1),
            startDate: moment().startOf('month').toISOString(),
            endDate: moment().endOf('month').toISOString()
          }
      this.upsertAccountSheetDialog.title = item ? 'Sửa giảng phí' : 'Tạo giảng phí'
      this.upsertAccountSheetDialog.isOpen = true
      this.upsertAccountSheetDialog.callback = (async () => {
        if (this.$refs.upsertAccountSheetDialog.validate()) {
          try {
            if (item) {
              await this.updateAccountSheet(this.upsertAccountSheetDialog.currentItem)
              this.$alert.success('Cập nhật thành công')
            } else {
              await this.createAccountSheet({
                data: this.upsertAccountSheetDialog.currentItem
              })
              this.$alert.success('Tạo thành công')
            }
          } catch (error) {
            console.error(error)
            this.$alert.error('Có lỗi xay ra')
          } finally {
            this.upsertAccountSheetDialog.isOpen = false
          }
        }
      }).bind(this)
    },
    onClickDeleteAccountSheet(item) {
      this.$dialog.confirm({
        title: 'Xóa bảng điểm',
        text: ` Bạn có Chắc Chắn xóa giảng phí và các thông tin liên quan?`,
        okText: 'Có',
        cancelText: 'Hủy',
        done: async () => {
          await this.removeAccountSheet(item.id)
          this.$alert.success('Xóa thành công')
        }
      })
    }
  },
  computed: {
    ...mapState('accountsheet', ['isLoadingAccountSheets', 'accountSheets', 'accountSheetRows']),
    ...mapGetters('auth', ['role']),
    ...mapState('staff', ['staffs']),
    ...mapState('app', ['department', 'roles']),
    ...mapState('accountant', ['accountants']),
    isSuperOrAccountant() {
      if (!this.role) {
        return false
      }
      return ['super_admin', 'accountant'].includes(this.role.type)
    },

    tableData() {
      return this.accountSheets
        .map((i, index) => ({ ...i, index: index + 1 }))
        .filter(({ title, note = '' }) => {
          if (!this.search) {
            return true
          }
          let regex = new RegExp(this.search, 'i')
          if (title.match(regex)) {
            return true
          }
          regex = new RegExp(this.search.replace(/\s+/g, ' ').trim(), 'i')
          if (title.match(regex) || note.match(regex)) {
            return true
          }
          return false
        })
    },
    isEmpty() {
      return !this.tableData.length
    },
    headers() {
      return [
        { text: 'STT', align: 'left', value: 'index' },
        { text: 'Tiêu đề', value: 'title' },
        { text: 'Ngày bắt đầu', value: 'startDate' },
        { text: 'Kế toán phụ trách', value: 'accountant.name' },
        { text: '', value: 'status' },
        { text: '', value: 'actions', width: 40 }
      ]
    }
  },
  created() {
    this.fetchAccountSheets()
    this.fetchAccountants()
  }
}
</script>
